import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronRight, Download, HelpCircle, Triangle } from "lucide-react";
import { downloadCSV, ShopeePeriodFilter, shopeeRange, initPicked } from "./components";

const METRICS = [
  { key: "sales", label: "Penjualan", money: true, color: "#2563EB" },
  { key: "txn", label: "Pesanan", money: false, color: "#60A5FA" },
  { key: "qty", label: "Produk Terjual", money: false, color: "#10B981" },
  { key: "avg", label: "Rata-rata / Pesanan", money: true, color: "#F59E0B" },
];

const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function Delta({ cur, prev }) {
  if (!prev) return <span className="text-[11px] text-slate-300">—</span>;
  const pct = ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span className={`text-xs font-semibold flex items-center gap-1 ${up ? "text-emerald-600" : "text-red-500"}`}>
      <Triangle className={`w-2 h-2 fill-current ${up ? "" : "rotate-180"}`} />
      {Math.abs(pct).toFixed(2).replace(".", ",")}%
    </span>
  );
}

export default function POSDashboard() {
  const nav = useNavigate();
  const [mode, setMode] = useState("yesterday");
  const [picked, setPicked] = useState(initPicked());
  const [sel, setSel] = useState(["sales", "txn"]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => shopeeRange(mode, picked), [mode, picked]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/pos/dashboard", { params: { date_from: range.from, date_to: range.to } })
      .then((r) => { if (alive) setData(r.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range.from, range.to]);

  useEffect(() => {
    if (mode !== "realtime") return undefined;
    const t = setInterval(() => {
      api.get("/pos/dashboard", { params: { date_from: range.from, date_to: range.to } }).then((r) => setData(r.data));
    }, 30000);
    return () => clearInterval(t);
  }, [mode, range.from, range.to]);

  const toggle = (key) => setSel((s) => {
    if (s.includes(key)) return s.length > 1 ? s.filter((k) => k !== key) : s;
    if (s.length >= 2) { toast.error("Maksimal 2 kriteria untuk grafik"); return s; }
    return [...s, key];
  });

  const xLabel = (s) => {
    if (data.days <= 2) return `${s.label}:00`;
    if (s.label.length === 7) {
      const [y, m] = s.label.split("-");
      return `${MONTHS_ID[Number(m) - 1]} ${y.slice(2)}`;
    }
    return s.label.slice(5).split("-").reverse().join("/");
  };

  const chartData = (data?.series || []).map((s) => ({
    label: xLabel(s),
    sales: s.sales,
    txn: s.txn,
    qty: s.qty || 0,
    avg: s.txn ? Math.round(s.sales / s.txn) : 0,
  }));
  const spark = data?.today_series || [];
  const nameOf = Object.fromEntries(METRICS.map((m) => [m.key, m.label]));
  const moneyOf = Object.fromEntries(METRICS.map((m) => [m.key, m.money]));

  return (
    <div data-testid="pos-dashboard" className="space-y-4">
      {/* Bar filter gaya Shopee */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-1">Periode Data</span>
        <ShopeePeriodFilter mode={mode} setMode={setMode} picked={picked} setPicked={setPicked} loading={loading} testid="pos-period" />
        <div className="flex-1" />
        <button
          data-testid="pos-dashboard-download"
          onClick={() => downloadCSV(`pos-dashboard-${range.from}-sd-${range.to}.csv`, ["Periode", ...sel.map((k) => nameOf[k])], chartData.map((c) => [c.label, ...sel.map((k) => c[k])]))}
          className="h-9 px-3.5 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-600 hover:border-slate-300 flex items-center gap-1.5 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download Data
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 items-start">
        {/* Kiri: kriteria utama + grafik */}
        <div className="xl:col-span-3">
          <div className="font-display font-bold text-slate-900 mb-2">Kriteria Utama</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {METRICS.map((m) => {
              const active = sel.includes(m.key);
              return (
                <button
                  key={m.key}
                  data-testid={`pos-kpi-${m.key}`}
                  onClick={() => toggle(m.key)}
                  className="relative text-left bg-white rounded-md border border-slate-200 p-3.5 transition-all hover:shadow-md"
                >
                  {active && <span className="absolute top-0 left-0 right-0 h-[3px] rounded-t-md" style={{ background: m.color }} />}
                  <div className="flex items-center gap-1 text-[13px] text-slate-700">
                    {m.label} <HelpCircle className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                  <div className="mt-1 text-lg font-bold text-slate-900 truncate">
                    {data ? (m.money ? formatIDR(data.current[m.key]) : data.current[m.key]) : "…"}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">vs Hari Sebelumnya</span>
                    {data && <Delta cur={data.current[m.key]} prev={data.previous[m.key]} />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
            <div className="font-display font-bold text-slate-900">Grafik setiap Kriteria</div>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              {sel.map((k) => {
                const m = METRICS.find((x) => x.key === k);
                return (
                  <span key={k} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />{m.label}
                  </span>
                );
              })}
              <span className="text-slate-400" data-testid="pos-chart-criteria-count">Kriteria Dipilih {sel.length}/4</span>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 mt-2 p-3 h-72" data-testid="pos-sales-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} minTickGap={40} />
                <YAxis yAxisId="l" hide />
                <YAxis yAxisId="r" orientation="right" hide />
                <Tooltip
                  formatter={(value, name) => [moneyOf[name] ? formatIDR(value) : value, name]}
                  contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }}
                />
                {sel.map((k, i) => {
                  const m = METRICS.find((x) => x.key === k);
                  return (
                    <Line
                      key={k}
                      yAxisId={i === 0 ? "l" : "r"}
                      type="monotone"
                      dataKey={k}
                      name={m.key}
                      stroke={m.color}
                      strokeWidth={2}
                      dot={{ r: 2, fill: m.color, strokeWidth: 0 }}
                      activeDot={{ r: 4 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Kanan: metrik real-time */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 h-fit" data-testid="pos-realtime-card">
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-slate-900">Metrik Real-time</span>
            <button data-testid="pos-realtime-more" onClick={() => nav("/admin/pos/reports")} className="text-xs text-blue-600 flex items-center hover:underline">
              Lainnya <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Update secara real-time
          </div>

          <div className="mt-3">
            <div className="flex items-center gap-1 text-xs text-slate-500">Penjualan Hari Ini <HelpCircle className="w-3 h-3 text-slate-300" /></div>
            <div className="text-lg font-bold text-slate-900" data-testid="pos-today-sales">{data ? formatIDR(data.today.sales) : "…"}</div>
            <div className="h-10 -mx-1 mt-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <Area type="monotone" dataKey="sales" stroke="#F59E0B" fill="#FEF3C7" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>00:00</span><span>12:00</span><span>24:00</span></div>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-4 mt-5">
            <div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500">Pesanan <HelpCircle className="w-3 h-3 text-slate-300" /></div>
              <div className="text-base font-bold text-slate-900">{data?.today.txn ?? "…"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500">Produk Terjual <HelpCircle className="w-3 h-3 text-slate-300" /></div>
              <div className="text-base font-bold text-slate-900">{data?.today.qty ?? "…"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500">Penjualan Tunai <HelpCircle className="w-3 h-3 text-slate-300" /></div>
              <div className="text-base font-bold text-slate-900">{data ? formatIDR(data.today_methods.cash) : "…"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500">Non-Tunai <HelpCircle className="w-3 h-3 text-slate-300" /></div>
              <div className="text-base font-bold text-slate-900">{data ? formatIDR(data.today_methods.noncash) : "…"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
